import "../helpers/secondarycss.css";
import Stack from "@mui/material/Stack";
import { useEffect, useState } from "react";
import { Theme } from "../data/style/Theme";
import * as routes from "../data/links/routes";
import Header from "../components/Index/Header";
import useScreenSize from "../context/ScreenContext";
import MainCard from "../components/Layouts/MainCard";
import {
  SpacedRow,
  CenteredRow,
  MainText,
  SecondaryTitle,
  MainTitle,
  AllCenteredColumn,
  AllCenteredRow,
  SmallTitle,
  StyledLink,
} from "../data/style/Style";

const LIVE_WORKOUTS = [
  {
    type: "AMRAP",
    level: "Rookie",
    icon: "♾",
    color: Theme.palette.up,
    timecap: 12,
    structure: null,
    exercises: [
      { reps: 10, name: "pull ups", rx: null },
      { reps: 15, name: "deadlift", rx: "80.0 / 55.0 kg" },
      { reps: 12, name: "box jumps", rx: null },
    ],
    stats: {
      athletes: 7,
      likes: 4,
      comments: 6,
    },
  },
  {
    type: "EMOM",
    level: "Scaled",
    icon: "⏱",
    color: Theme.palette.blue,
    timecap: 20,
    structure: null,
    exercises: [
      { reps: 8, min: "M1", name: "squat snatch", rx: "40.0 / 25.0 kg" },
      { reps: 5, min: "M2", name: "bar muscle ups", rx: null },
      { reps: 14, min: "M3", name: "cal row", rx: null },
      { reps: 10, min: "M4", name: "wallballs", rx: "9 / 6 kg" },
    ],
    stats: {
      athletes: 22,
      likes: 18,
      comments: 22,
    },
  },
  {
    type: "For Time",
    level: "Intermediate",
    icon: "⚡",
    color: Theme.palette.gold,
    timecap: 11,
    structure: "1-2-3-4-5-6-7-8-9-10",
    exercises: [
      { reps: null, name: "power snatch", rx: "50.0 / 30.0 kg" },
      { reps: null, name: "toes to bar", rx: null },
    ],
    stats: {
      athletes: 20,
      likes: 15,
      comments: 10,
    },
  },
  {
    type: "Intervals",
    level: "Rx",
    icon: "🔄",
    color: Theme.palette.orange,
    timecap: 24,
    structure: "4 × 3 min on / 3 min rest",
    exercises: [
      { reps: 10, name: "thrusters", rx: "42.5 / 30.0 kg" },
      { reps: 10, name: "burpees over bar", rx: null },
      { reps: 30, name: "double unders", rx: null },
    ],
    stats: {
      athletes: 10,
      likes: 5,
      comments: 14,
    },
  },
];

const LEVELS = [
  {
    key: "Rookie",
    color: Theme.palette.up,
    icon: "🌱",
    desc: "New to functional fitness or returning after a break. You're learning movement patterns, building your engine and figuring out what your body can do. Every workout is a win — showing up is the hardest part, and you're already doing it.",
  },
  {
    key: "Scaled",
    color: Theme.palette.blue,
    icon: "📈",
    desc: "You've got the foundations locked in and you're training consistently. You modify loads and movements when needed, not because you're weak — but because you're smart about long-term progress. You're building the base that Rx is built on.",
  },
  {
    key: "Intermediate",
    color: Theme.palette.gold,
    icon: "⚡",
    desc: "You're comfortable with the barbell, stringing together gymnastic skills and pushing your conditioning hard. Most workouts are done close to Rx standards. You've earned your calluses and you know the difference between scaling and sandbagging.",
  },
  {
    key: "Rx",
    color: Theme.palette.orange,
    icon: "🔥",
    desc: "You perform workouts exactly as prescribed — no modifications, no excuses. Barbell cycling, muscle-ups, heavy singles — you've put in the years. You're not just training to finish; you're training to compete. The leaderboard is your benchmark.",
  },
];

const FEATURES = [
  {
    icon: "⚡",
    title: "Smart Workout Generator",
    color: Theme.palette.gold,
    desc: "The generator creates personalised workouts based on your level, equipment and preferred style. Every session is unique.",
  },
  {
    icon: "📈",
    title: "Performance Analytics",
    color: Theme.palette.blue,
    desc: "Track your scores, chart your progress over time and see exactly how you're improving across every exercise category.",
  },
  {
    icon: "🏆",
    title: "Achievements & Milestones",
    color: Theme.palette.orange,
    desc: "Unlock bronze, silver and gold medals as you hit new personal bests. A full achievement system that keeps you motivated.",
  },
  {
    icon: "⚔️",
    title: "Athlete Challenges",
    color: Theme.palette.down,
    desc: "Challenge your training partners directly on any workout. See who's fastest, strongest and most consistent.",
  },
  {
    icon: "📊",
    title: "Live Leaderboards",
    color: Theme.palette.up,
    desc: "Compete in real-time weekly standings. Male and female boards, filtered by your gym or global community.",
  },
  {
    icon: "⚙️",
    title: "Scaling Reference",
    color: Theme.palette.purple,
    desc: "Built-in scaling guide suggests the right modification for every movement — so no athlete gets left behind.",
  },
];

const Index = () => {
  const { mobileDevice } = useScreenSize();
  const [idx, setIdx] = useState(0);
  const workout = LIVE_WORKOUTS[idx];

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeout(() => {
        setIdx((i) => (i + 1) % LIVE_WORKOUTS.length);
      }, 350);
    }, 3800);
    return () => clearInterval(interval);
  });

  return (
    <Stack
      sx={{
        minHeight: "100vh",
        position: "relative",
      }}
    >
      {/* Ambient glows */}
      <div
        style={{
          position: "absolute",
          top: -180,
          left: "50%",
          transform: "translateX(-50%)",
          width: 900,
          height: 700,
          borderRadius: "50%",
          background:
            "radial-gradient(ellipse,rgba(249,115,22,.16) 0%,rgba(249,115,22,.05) 40%,transparent 70%)",
          pointerEvents: "none",
          animation: "gpAmbient 5s ease-in-out infinite alternate",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "30%",
          right: -120,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background:
            "radial-gradient(ellipse,rgba(250,204,21,.08) 0%,transparent 65%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -100,
          left: -80,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background:
            "radial-gradient(ellipse,rgba(249,115,22,.07) 0%,transparent 65%)",
          pointerEvents: "none",
        }}
      />
      <Header />
      <Stack gap={2} alignItems={"flex-start"} width={"100%"} mt={12}>
        <CenteredRow pl={2} gap={1}>
          <div
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: Theme.palette.up,
              animation: "gpPulseRing 1.8s ease-in-out infinite",
            }}
          />
          <span
            style={{
              fontFamily: "DM Sans, sans-serif",
              fontSize: 12,
              fontWeight: 700,
              color: Theme.palette.up,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
            }}
          >
            Functional Fitness Tracker
          </span>
        </CenteredRow>
        <SpacedRow
          alignItems={"center"}
          flex={1}
          flexWrap={"wrap"}
          gap={4}
          pl={2}
          pr={2}
        >
          <Stack gap={1} alignItems={"center"} flex={1}>
            <h1
              style={{
                width: "80%",
                fontFamily: "Oswald, sans-serif",
                color: Theme.palette.text,
                fontWeight: 900,
                fontSize: "clamp(46px,6.5vw,82px)",
                lineHeight: 0.9,
                letterSpacing: "-0.02em",
                textTransform: "uppercase",
                marginBottom: 22,
                animation: "gpFadeUp .7s ease .12s both",
              }}
            >
              Train
              <br />
              Track
              <br />
              <span className="shimmer-text">Dominate</span>
            </h1>
            <MainText sx={{ color: Theme.palette.muted + " !important" }}>
              The complete functional fitness companion. Auto-generated
              workouts, real-time leaderboards, personal bests tracking and
              athlete challenges — all in one place.
            </MainText>
          </Stack>
          <Stack gap={2} flex={1}>
            <CenteredRow width={"100%"} gap={1}>
              <div
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: Theme.palette.orange,
                  animation: "gpPulseRing 1.8s ease-in-out infinite",
                }}
              />
              <SecondaryTitle>live workout preview</SecondaryTitle>
            </CenteredRow>
            <MainCard start={workout.color} middle={workout.color}>
              <Stack gap={1}>
                <SpacedRow>
                  <CenteredRow gap={2}>
                    <MainText fontSize={"1.2rem"}>{workout.icon}</MainText>
                    <Stack>
                      <MainTitle>{workout.type}</MainTitle>
                      <MainText
                        fontSize={"0.8rem"}
                        sx={{ color: Theme.palette.muted }}
                      >
                        Timecap:&nbsp;
                        <span style={{ color: workout.color }}>
                          {workout.timecap} min
                        </span>
                      </MainText>
                    </Stack>
                  </CenteredRow>
                  <Stack gap={1}>
                    <AllCenteredColumn
                      p={0.5}
                      sx={{
                        border: 1,
                        borderColor: workout.color,
                        background: workout.color + "07",
                        borderRadius: 3,
                      }}
                    >
                      <MainText
                        sx={{
                          color: workout.color + " !important",
                          fontSize: "0.7rem",
                        }}
                      >
                        {workout.level}
                      </MainText>
                    </AllCenteredColumn>
                    <AllCenteredRow
                      p={0.5}
                      sx={{
                        border: 1,
                        borderColor: Theme.palette.up,
                        background: Theme.palette.up + "07",
                        borderRadius: 3,
                      }}
                    >
                      <MainText
                        sx={{
                          color: Theme.palette.up + " !important",
                          fontSize: "0.7rem",
                        }}
                      >
                        🌐 Public
                      </MainText>
                    </AllCenteredRow>
                  </Stack>
                </SpacedRow>
                <Stack height={"40px"}>
                  <MainText
                    alignSelf={"center"}
                    fontSize={"1.2rem"}
                    fontWeight={700}
                    sx={{ color: workout.color + " !important" }}
                  >
                    {workout.structure}
                  </MainText>
                </Stack>
                <Stack gap={1} height={"25vh"}>
                  {workout.exercises.map((item, index) => (
                    <SpacedRow
                      key={index}
                      alignItems={"center"}
                      p={1}
                      sx={{
                        background: Theme.palette.card,
                        border: 1,
                        borderColor: Theme.palette.border,
                        borderRadius: 3,
                      }}
                    >
                      <MainText>{item.name}</MainText>
                      <MainText
                        fontSize={"0.8rem"}
                        sx={{ color: Theme.palette.muted + " !important" }}
                      >
                        {item.rx}
                      </MainText>
                    </SpacedRow>
                  ))}
                </Stack>
                <CenteredRow mt={2} gap={2}>
                  <CenteredRow gap={1}>
                    <MainText>❤️</MainText>
                    <SecondaryTitle>{workout.stats.likes}</SecondaryTitle>
                  </CenteredRow>
                  <CenteredRow gap={1}>
                    <MainText>💬</MainText>
                    <SecondaryTitle>{workout.stats.comments}</SecondaryTitle>
                  </CenteredRow>
                  <CenteredRow gap={1}>
                    <MainText>🏃</MainText>
                    <SecondaryTitle>{workout.stats.athletes}</SecondaryTitle>
                  </CenteredRow>
                </CenteredRow>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 6,
                  }}
                >
                  {LIVE_WORKOUTS.map((w, i) => (
                    <div
                      key={i}
                      style={{
                        width: i === idx ? 20 : 6,
                        height: 6,
                        borderRadius: 99,
                        background:
                          i === idx ? workout.color : Theme.palette.dim,
                        transition: "all .35s ease",
                      }}
                    />
                  ))}
                </div>
              </Stack>
            </MainCard>
            <SpacedRow gap={1} flex={1} minWidth={"300px"}>
              <a
                href={process.env.REACT_APP_ANDROID_APP_URL}
                style={{ textDecoration: "none", flex: 1 }}
                target="_blank"
                rel="noreferrer"
              >
                <CenteredRow
                  gap={2}
                  flex={1}
                  p={1}
                  sx={{
                    background: Theme.palette.card,
                    border: 1,
                    borderColor: Theme.palette.border,
                    borderRadius: 3,
                  }}
                >
                  <MainText fontSize={mobileDevice ? "1.2rem" : "1.5rem"}>
                    🤖
                  </MainText>
                  <Stack>
                    <SecondaryTitle>GET IT ON</SecondaryTitle>
                    <MainTitle>Google Play</MainTitle>
                  </Stack>
                </CenteredRow>
              </a>
              <a
                href={process.env.REACT_APP_IOS_APP_URL}
                style={{ textDecoration: "none", flex: 1 }}
                target="_blank"
                rel="noreferrer"
              >
                <CenteredRow
                  gap={2}
                  flex={1}
                  p={1}
                  sx={{
                    background: Theme.palette.card,
                    border: 1,
                    borderColor: Theme.palette.border,
                    borderRadius: 3,
                  }}
                >
                  <MainText fontSize={"1.5rem"}>🍎</MainText>
                  <Stack>
                    <SecondaryTitle>Download on</SecondaryTitle>
                    <MainTitle>App Store</MainTitle>
                  </Stack>
                </CenteredRow>
              </a>
            </SpacedRow>
          </Stack>
        </SpacedRow>
        <Stack
          alignSelf={"center"}
          alignItems={"center"}
          gap={1}
          mt={4}
          sx={{
            animation: "gpFadeIn 1s ease 1.2s both",
          }}
        >
          <SmallTitle fontSize={"0.7rem"}>Scroll to explore</SmallTitle>
          <Stack gap={0.5}>
            {[0, 0.18, 0.36].map((d, i) => (
              <div
                key={i}
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: Theme.palette.orange,
                  opacity: 0.6,
                  animation: `gpDotDance 1.2s ease-in-out ${d}s infinite`,
                }}
              />
            ))}
          </Stack>
        </Stack>
        <Stack
          mt={2}
          width={"35%"}
          minWidth={"350px"}
          alignItems={"center"}
          alignSelf={"center"}
        >
          <MainText
            p={1}
            fontSize={"0.8rem"}
            fontWeight={600}
            sx={{
              color: Theme.palette.orange + " !important",
              border: 1,
              textTransform: "uppercase",
              borderColor: Theme.palette.orange,
              borderRadius: 4,
              background: Theme.palette.orange + "22",
            }}
          >
            🏋 Everything You Need
          </MainText>
          <h1
            style={{
              textAlign: "center",
              marginTop: "30px",
              fontFamily: "Oswald, sans-serif",
              color: Theme.palette.text,
              fontWeight: 900,
              fontSize: "clamp(30px,6.5vw,55px)",
              lineHeight: 0.9,
              letterSpacing: "-0.02em",
              textTransform: "uppercase",
              marginBottom: "10px",
              animation: "gpFadeUp .7s ease .12s both",
            }}
          >
            Built for athletes
            <br />
            <span className="shimmer-text">Built to last</span>
          </h1>
          <MainText
            sx={{
              color: Theme.palette.muted + " !important",
              textAlign: "center",
            }}
          >
            Every feature is designed around how functional fitness athletes
            actually train, compete and improve.
          </MainText>
        </Stack>
        <SpacedRow
          mt={2}
          width={"80%"}
          gap={3}
          minWidth={"350px"}
          flexWrap={"wrap"}
          alignSelf={"center"}
        >
          {FEATURES.map((item, index) => (
            <FeatureCard key={index} item={item} />
          ))}
        </SpacedRow>
        <Stack
          mt={4}
          minWidth={"370px"}
          width={"40%"}
          alignItems={"center"}
          alignSelf={"center"}
        >
          <MainText
            p={1}
            fontSize={"0.8rem"}
            fontWeight={600}
            sx={{
              color: Theme.palette.blue + " !important",
              border: 1,
              textTransform: "uppercase",
              borderColor: Theme.palette.blue,
              borderRadius: 4,
              background: Theme.palette.blue + "22",
            }}
          >
            ⬆ Training levels
          </MainText>
          <h1
            style={{
              textAlign: "center",
              marginTop: "30px",
              fontFamily: "Oswald, sans-serif",
              color: Theme.palette.text,
              fontWeight: 900,
              fontSize: "clamp(30px,6.5vw,55px)",
              lineHeight: 0.9,
              letterSpacing: "-0.02em",
              textTransform: "uppercase",
              marginBottom: "10px",
              animation: "gpFadeUp .7s ease .12s both",
            }}
          >
            your level. your rules
          </h1>
        </Stack>
        <SpacedRow
          width={"80%"}
          alignSelf={"center"}
          flexWrap={"wrap"}
          mt={2}
          gap={3}
        >
          {LEVELS.map((item, index) => (
            <Stack key={index} flex={1} minWidth={"250px"} gap={1}>
              <Stack alignItems={"flex-start"} direction={"row"} gap={1}>
                <AllCenteredColumn
                  p={1}
                  sx={{
                    background: item.color + "22",
                    border: 1,
                    borderColor: item.color,
                    borderRadius: 4,
                  }}
                >
                  {item.icon}
                </AllCenteredColumn>
                <Stack gap={2}>
                  <MainTitle>{item.key}</MainTitle>
                  <MainText sx={{ color: Theme.palette.muted + " !important" }}>
                    {item.desc}
                  </MainText>
                </Stack>
              </Stack>
            </Stack>
          ))}
        </SpacedRow>
        <Footer />
      </Stack>
    </Stack>
  );
};

export default Index;

const FeatureCard = ({ item }) => {
  return (
    <Stack
      flex={1}
      minWidth={"300px"}
      alignItems={"flex-start"}
      gap={2}
      p={2}
      sx={{
        position: "relative",
        borderRadius: 3,
        transition: "all .24s ease",
        border: 1,
        borderColor: Theme.palette.border,
        background: Theme.palette.card,
        "&:hover": {
          borderColor: item.color,
          transform: "translateY(-4px)",
          boxShadow: `0 12px 30px ${item.color} !important`,
        },
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 5,
          right: 0,
          height: 2,
          background: `linear-gradient(90deg, ${item.color}, ${item.color}33, transparent)`,
        }}
      />
      <AllCenteredColumn
        sx={{
          background: item.color + "22",
          border: 1,
          borderColor: item.color,
          borderRadius: 4,
        }}
        p={1}
      >
        {item.icon}
      </AllCenteredColumn>
      <MainTitle>{item.title}</MainTitle>
      <MainText sx={{ color: Theme.palette.muted + " !important" }}>
        {item.desc}
      </MainText>
    </Stack>
  );
};

const Footer = () => {
  const { size, mobileDevice } = useScreenSize();
  let [width] = size;
  return (
    <Stack
      direction={"row"}
      sx={{ borderTop: 1, borderColor: Theme.palette.border }}
      alignItems={"center"}
      justifyContent={mobileDevice || width < 450 ? "center" : "space-between"}
      flexWrap={"wrap"}
      height={"80px"}
      width={"100%"}
    >
      <SecondaryTitle ml={2}>© 2025 Grind Partner</SecondaryTitle>
      <Stack direction={"row"} gap={2}>
        <StyledLink href={`mailto:${process.env.REACT_APP_CONTACT_EMAIL}`}>
          <SecondaryTitle>Contact</SecondaryTitle>
        </StyledLink>
        <StyledLink href={routes.SCALING}>
          <SecondaryTitle>Scaling</SecondaryTitle>
        </StyledLink>
        <StyledLink href={routes.POLICY}>
          <SecondaryTitle>Privacy</SecondaryTitle>
        </StyledLink>
        <StyledLink href={routes.TERMS}>
          <SecondaryTitle>Terms</SecondaryTitle>
        </StyledLink>
      </Stack>
      <SecondaryTitle ml={2} mr={2}>
        Train Hard. Recover Smart.
      </SecondaryTitle>
    </Stack>
  );
};
